import { Text } from "@/components/ui/baseUi/text"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { ArticleCard } from "@modules/article/components/articleCard"
import { useArticle } from "@modules/article/hooks/useArticle"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useCallback, useEffect, useRef } from "react"

type NewsResumeProps = {
  className?: string
} & React.ComponentProps<"div">

export function NewsResume({ className, ...props }: NewsResumeProps) {
  const { articleList, fetchNextArticles, isFetchingNextPage, hasNextPage } = useArticle()

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage) {
        fetchNextArticles()
      }
    },
    [fetchNextArticles, hasNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection)

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [handleIntersection])

  return (
    <div
      className={cn(
        "mt-2 flex h-full flex-1 flex-col",
        "max-[1480px]:overflow-hidden",
        "min-[1480px]:overflow-y-auto",
        className,
      )}
      {...props}
    >
      <Text fontStyle="TL">{word("home.screen.articles.title")}</Text>
      <div className="grid grid-cols-1 justify-items-center gap-4 min-[980px]:max-[1180px]:grid-cols-2 min-[1180px]:max-[1480px]:grid-cols-3">
        {articleList?.map((article) => (
          <ArticleCard key={article._id} article={article} />
        ))}
      </div>
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="flex w-full flex-col items-center justify-center">
          <Text fontStyle="BM">{word("global.loading")}</Text>
          <LoadingSpinner className="h-auto" />
        </div>
      )}
    </div>
  )
}
